import { useMemo } from "react";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import { ensureDoctorPrefix } from "@/lib/doctorName";

export const EXTERNAL_DOCTOR_ID = "__EXTERNAL_DOCTOR__";
export const EXTERNAL_DOCTOR_LABEL = "External Doctor";

export type DoctorPickOption = {
  id: string;
  name: string;
  specialisation?: string | null;
  opdCharge?: { amount?: number | null } | number | null;
};

function doctorOpdFeeAmount(doctor: DoctorPickOption): number | undefined {
  if (typeof doctor.opdCharge === "number") return doctor.opdCharge;
  return doctor.opdCharge?.amount ?? undefined;
}

export function doctorsToSearchableOptions(
  doctors: DoctorPickOption[],
  opts?: { showOpdFee?: boolean; showSpecialisation?: boolean }
): SearchableSelectOption[] {
  const showFee = opts?.showOpdFee ?? false;
  const showSpec = opts?.showSpecialisation ?? true;
  return doctors.map((doctor) => {
    const fee = doctorOpdFeeAmount(doctor);
    const feeLabel =
      showFee && fee != null && fee > 0
        ? ` · OPD ₹${fee.toLocaleString("en-IN")}`
        : showFee
          ? " · OPD fee N/A"
          : "";
    const spec =
      showSpec && doctor.specialisation
        ? ` (${doctor.specialisation})`
        : showSpec
          ? " (No specialisation)"
          : "";
    const displayName = ensureDoctorPrefix(doctor.name);
    return {
      value: doctor.id,
      label: `${displayName}${spec}${feeLabel}`,
      searchText: [doctor.name, doctor.specialisation, displayName].filter(Boolean).join(" "),
    };
  });
}

type DoctorSearchableSelectProps = {
  doctors: DoctorPickOption[];
  value: string;
  onValueChange: (doctorId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  showOpdFee?: boolean;
  showSpecialisation?: boolean;
  className?: string;
  /** Append "External Doctor" as the last selectable option. */
  allowExternalDoctor?: boolean;
};

export function DoctorSearchableSelect({
  doctors,
  value,
  onValueChange,
  disabled,
  placeholder = "Select a doctor",
  id,
  showOpdFee = false,
  showSpecialisation = true,
  className,
  allowExternalDoctor = false,
}: DoctorSearchableSelectProps) {
  const options = useMemo(() => {
    const base = doctorsToSearchableOptions(doctors, {
      showOpdFee,
      showSpecialisation,
    });
    if (!allowExternalDoctor) return base;
    return [
      ...base,
      {
        value: EXTERNAL_DOCTOR_ID,
        label: EXTERNAL_DOCTOR_LABEL,
        searchText: "external doctor outside referral",
      },
    ];
  }, [doctors, showOpdFee, showSpecialisation, allowExternalDoctor]);

  return (
    <SearchableSelect
      id={id}
      value={value}
      onValueChange={onValueChange}
      options={options}
      placeholder={placeholder}
      searchPlaceholder="Type doctor name…"
      disabled={disabled}
      emptyMessage="No doctors match your search."
      className={className}
      contentClassName="z-[100]"
    />
  );
}
